import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { statsAPI } from "../lib/api";
import { formatDateTime, getDirectionLabel, getDirectionColor } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  FileText,
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  TrendingUp,
  PlusCircle,
  ExternalLink,
  Users,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await statsAPI.get();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48 bg-muted" />
          <Skeleton className="h-10 w-40 bg-muted" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const statsCards = [
    {
      title: "Totale Protocolli",
      value: stats?.total_protocols || 0,
      icon: FileText,
      color: "text-[#2D7BB7]",
      bgColor: "bg-[#2D7BB7]/10",
    },
    {
      title: "In Entrata",
      value: stats?.protocols_in || 0,
      icon: ArrowDownLeft,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: "In Uscita",
      value: stats?.protocols_out || 0,
      icon: ArrowUpRight,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      title: "Questo Mese",
      value: stats?.protocols_this_month || 0,
      icon: Calendar,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  const pieData = [
    { name: "Entrata", value: stats?.protocols_in || 0 },
    { name: "Uscita", value: stats?.protocols_out || 0 },
  ];

  const monthlyTrend = stats?.monthly_trend || [];
  const topContacts = stats?.top_contacts || [];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Panoramica del sistema di protocollo</p>
        </div>
        <Link to="/protocols/new">
          <Button
            className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md hover:shadow-lg transition-all duration-300"
            data-testid="new-protocol-btn"
          >
            <PlusCircle className="mr-2" size={18} />
            Nuovo Protocollo
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((card, index) => (
          <Card
            key={index}
            className="bg-card border-border hover:border-[#5BA4D4]/50 transition-all duration-300 hover:shadow-lg"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className="text-3xl font-heading font-bold text-foreground">
                    {card.value.toLocaleString("it-IT")}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${card.bgColor}`}>
                  <card.icon className={card.color} size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Trend Chart */}
      <Card className="bg-card border-border" data-testid="monthly-trend-chart">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <TrendingUp size={20} className="text-[#2D7BB7]" />
            Andamento Mensile (ultimi 12 mesi)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyTrend}>
                <defs>
                  <linearGradient id="colorEntrata" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorUscita" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="month"
                  stroke="#64748B"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#64748B" allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#0F172A", fontWeight: 600 }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="entrata"
                  name="Entrata"
                  stroke="#10B981"
                  strokeWidth={2}
                  fill="url(#colorEntrata)"
                />
                <Area
                  type="monotone"
                  dataKey="uscita"
                  name="Uscita"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  fill="url(#colorUscita)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Nessun dato disponibile
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* By Type Chart */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <FileText size={20} className="text-[#2D7BB7]" />
              Per Tipologia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.by_type} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="type"
                    stroke="#64748B"
                    width={100}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#0F172A" }}
                  />
                  <Bar dataKey="count" name="Protocolli" fill="#2D7BB7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Direction Pie Chart */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <ArrowDownLeft size={20} className="text-[#2D7BB7]" />
              Entrata / Uscita
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.protocols_in > 0 || stats?.protocols_out > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Contacts */}
        <Card className="bg-card border-border lg:col-span-1" data-testid="top-contacts-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Users size={20} className="text-[#2D7BB7]" />
              Top Contatti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topContacts.length > 0 ? (
              <div className="space-y-3">
                {topContacts.map((contact, i) => {
                  const maxCount = topContacts[0]?.count || 1;
                  const percentage = (contact.count / maxCount) * 100;
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground font-medium truncate mr-2">
                          {contact.name}
                        </span>
                        <span className="text-muted-foreground flex-shrink-0">
                          {contact.count}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#2D7BB7] rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun contatto associato</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading text-foreground">
            Attivita Recente
          </CardTitle>
          <Link to="/protocols">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              Vedi tutti
              <ExternalLink size={16} className="ml-2" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_activity.map((protocol, index) => (
                <Link
                  key={protocol.id || index}
                  to={`/protocols/${protocol.id}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-border hover:border-[#5BA4D4]/50"
                  data-testid={`recent-protocol-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="font-mono text-sm text-[#2D7BB7] font-medium">
                      {protocol.protocol_number}
                    </div>
                    <div>
                      <p className="text-foreground font-medium">{protocol.subject}</p>
                      <p className="text-sm text-muted-foreground">
                        {protocol.contact_name || protocol.sender || protocol.recipient || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge
                      className={`${getDirectionColor(protocol.direction)} border`}
                    >
                      {getDirectionLabel(protocol.direction)}
                    </Badge>
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                      {formatDateTime(protocol.created_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun protocollo registrato</p>
              <Link to="/protocols/new">
                <Button
                  variant="outline"
                  className="mt-4 border-border text-foreground hover:bg-muted"
                >
                  Registra il primo protocollo
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
